import { useTranslation } from "react-i18next";
import { Field, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type BidLimits, currencyLabel, customBidWithinLimits } from "./money";
import type { BidMode, UpdateDraftField } from "./sponsored-display-draft";

export function TargetingBidControls({
  bidMode,
  currency,
  customBid,
  bidLimits,
  onUpdate,
  onApplyBid,
}: {
  bidMode: BidMode;
  currency: string;
  customBid: string;
  bidLimits: BidLimits;
  onUpdate: UpdateDraftField;
  onApplyBid: (value: number | null) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div
      className="flex shrink-0 flex-nowrap items-center gap-3 border-b border-line bg-subtle/25 px-4 py-3"
      data-slot="sd-targeting-bid-controls"
    >
      <Field
        className="w-auto flex-none flex-nowrap items-center gap-3"
        data-slot="sd-targeting-bid-mode-field"
        orientation="horizontal"
      >
        <FieldLabel className="shrink-0" htmlFor="sd-bid-mode">
          {t("sd.targeting.bidSetting")}
        </FieldLabel>
        <Select
          items={[
            { value: "suggested", label: t("sd.targeting.suggestedBid") },
            { value: "custom", label: t("sd.targeting.customBid") },
          ]}
          value={bidMode}
          onValueChange={(value) => {
            if (value === "suggested" || value === "custom") {
              onUpdate("bidMode", value);
              if (value === "custom") {
                const parsedBid = Number(customBid);
                const nextBid = Number.isFinite(parsedBid) && parsedBid > 0 ? parsedBid : 0.75;
                onUpdate("customBid", String(nextBid));
                onApplyBid(nextBid);
              } else {
                onApplyBid(null);
              }
            }
          }}
        >
          <SelectTrigger id="sd-bid-mode" className="w-56 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="suggested">{t("sd.targeting.suggestedBid")}</SelectItem>
              <SelectItem value="custom">{t("sd.targeting.customBid")}</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </Field>
      {bidMode === "custom" ? (
        <Field
          className="w-44 flex-none"
          data-invalid={!customBidWithinLimits(customBid, bidLimits)}
          data-slot="sd-targeting-custom-bid-field"
        >
          <FieldLabel className="sr-only" htmlFor="sd-custom-bid">
            {t("sd.targeting.bid")}
          </FieldLabel>
          <InputGroup>
            <InputGroupAddon>{currencyLabel(currency)}</InputGroupAddon>
            <InputGroupInput
              aria-invalid={!customBidWithinLimits(customBid, bidLimits)}
              id="sd-custom-bid"
              min="0"
              step="0.01"
              type="number"
              value={customBid}
              onChange={(event) => {
                onUpdate("customBid", event.target.value);
                const value = Number(event.target.value);
                onApplyBid(Number.isFinite(value) && value > 0 ? value : null);
              }}
            />
          </InputGroup>
        </Field>
      ) : null}
    </div>
  );
}
